import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { KeyRound, ShieldCheck, ArrowRight, Eye, EyeOff, CreditCard, Lock, CheckCircle, AlertCircle, Building2 } from 'lucide-react';

export default function SetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();

    const email = searchParams.get('email') || '';
    const token = searchParams.get('token') || '';
    const restaurantId = searchParams.get('restaurantId') || '';

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchingInfo, setFetchingInfo] = useState(true);
    const [success, setSuccess] = useState(false);
    const [invitationInfo, setInvitationInfo] = useState(null);

    useEffect(() => {
        const fetchInfo = async () => {
            if (!email || !token || !restaurantId) {
                setFetchingInfo(false);
                return;
            }
            try {
                const res = await api.get('/api/auth/invitation-info', {
                    params: { email, token, restaurantId }
                });
                setInvitationInfo(res.data);
            } catch (err) {
                console.error(err);
                toast.error(err.response?.data?.message || 'Invalid or expired activation link');
            } finally {
                setFetchingInfo(false);
            }
        };
        fetchInfo();
    }, [email, token, restaurantId]);

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePasswordAndCardSubmit = async (e) => {
        e.preventDefault();
        
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecial = /[^A-Za-z0-9]/.test(password);

        if (password.length < 8 || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
            return toast.error('Password must be at least 8 characters long and include an uppercase letter, lowercase letter, number, and special character.');
        }
        if (password !== confirmPassword) {
            return toast.error('Passwords do not match.');
        }

        setLoading(true);
        try {
            // Load Razorpay Checkout SDK
            const loaded = await loadRazorpayScript();
            if (!loaded) {
                toast.error('Failed to load Razorpay Checkout SDK. Please check internet connection.');
                setLoading(false);
                return;
            }

            const razorpayKey = invitationInfo?.razorpayKey || 'rzp_test_SiwDxnw6qUPSe7';
            const subscriptionId = invitationInfo?.subscriptionId;

            if (subscriptionId) {
                // Open Razorpay Subscription Mandate Window
                const options = {
                    key: razorpayKey,
                    subscription_id: subscriptionId,
                    name: invitationInfo?.franchiseName || 'Aaharam Cloud',
                    description: `Mandate Card for ${invitationInfo?.restaurantName || 'Outlet'} (${invitationInfo?.planName || 'Starter'} Plan)`,
                    image: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png',
                    prefill: {
                        name: invitationInfo?.restaurantName || 'Branch Admin',
                        email: email
                    },
                    config: {
                        display: {
                            blocks: {
                                cardBlock: {
                                    name: 'Card Payment Only',
                                    instruments: [
                                        {
                                            method: 'card'
                                        }
                                    ]
                                }
                            },
                            sequence: ['block.cardBlock'],
                            preferences: {
                                show_default_blocks: false
                            }
                        }
                    },
                    handler: async (response) => {
                        try {
                            await api.post('/api/auth/reset-password', {
                                email,
                                otp: token,
                                newPassword: password,
                                restaurantId,
                                razorpay_payment_id: response.razorpay_payment_id || '',
                                razorpay_subscription_id: response.razorpay_subscription_id || subscriptionId,
                                razorpay_signature: response.razorpay_signature || ''
                            });
                            toast.success('Your account and subscription card mandate have been activated successfully!');
                            setSuccess(true);
                            setTimeout(() => {
                                navigate('/login');
                            }, 3000);
                        } catch (apiErr) {
                            console.error(apiErr);
                            toast.error(apiErr.response?.data?.message || 'Failed to complete account activation');
                            setLoading(false);
                        }
                    },
                    modal: {
                        ondismiss: () => {
                            toast.info('Card authorization cancelled. Mandatory authorization required to activate.');
                            setLoading(false);
                        }
                    },
                    theme: {
                        color: '#4f46e5'
                    }
                };

                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                // Fallback direct setup
                await api.post('/api/auth/reset-password', {
                    email,
                    otp: token,
                    newPassword: password,
                    restaurantId
                });
                toast.success('Password configured successfully!');
                setSuccess(true);
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to set password. Link may be invalid or expired.');
            setLoading(false);
        }
    };

    if (fetchingInfo) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
                <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl border border-slate-100 space-y-6 animate-scale-up">
                    <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto">
                        <ShieldCheck className="w-8 h-8" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-900">Branch Outlet Activated!</h2>
                        <p className="text-slate-500 text-sm mt-2">Password configured & Razorpay subscription mandate authorized successfully. Redirecting to login...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
            <div className="bg-white rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-xl border border-slate-100 space-y-6">
                <div className="text-center space-y-2">
                    <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mx-auto">
                        <Building2 className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900">Branch Outlet Activation</h2>
                    <p className="text-slate-500 text-xs sm:text-sm">Set your admin password & authorize card details via Razorpay to activate your outlet. (Activation link valid for 7 days)</p>
                </div>

                {invitationInfo && (
                    <div className="p-4 bg-slate-900 text-white rounded-2xl space-y-2">
                        <div className="flex justify-between items-center text-xs text-slate-400">
                            <span>{invitationInfo.franchiseName}</span>
                            <span className="bg-emerald-500/20 text-emerald-400 font-bold px-2 py-0.5 rounded-full text-[10px] uppercase">{invitationInfo.planName} Plan</span>
                        </div>
                        <h3 className="text-lg font-black text-white">{invitationInfo.restaurantName}</h3>
                        <div className="flex justify-between items-center text-xs pt-1 border-t border-slate-800">
                            <span className="text-slate-400">Monthly Plan Billing:</span>
                            <span className="font-bold text-amber-400">₹{(invitationInfo.planPrice || 2999).toLocaleString()}/month</span>
                        </div>
                    </div>
                )}

                <div className="p-3.5 bg-amber-50 rounded-2xl border border-amber-200 flex items-start gap-3">
                    <CreditCard className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 font-medium">
                        <strong>Mandatory Step:</strong> Clicking below will save your password and open the secure Razorpay payment window to authorize card details for your outlet.
                    </p>
                </div>

                <form onSubmit={handlePasswordAndCardSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Admin Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                required
                                placeholder="Min. 8 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 pr-12 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>

                        {/* Password Strength Checklist */}
                        {password && (() => {
                            const criteria = {
                                length: password.length >= 8,
                                uppercase: /[A-Z]/.test(password),
                                lowercase: /[a-z]/.test(password),
                                number: /[0-9]/.test(password),
                                special: /[^A-Za-z0-9]/.test(password),
                            };
                            const passedCount = Object.values(criteria).filter(Boolean).length;
                            const strengthPercentage = (passedCount / 5) * 100;
                            return (
                                <div className="space-y-2 mt-2">
                                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full transition-all duration-300 ${
                                                passedCount <= 2 ? 'bg-rose-500' : passedCount <= 4 ? 'bg-amber-500' : 'bg-emerald-500'
                                            }`}
                                            style={{ width: `${strengthPercentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1.5 text-[10px] font-bold text-slate-500">
                                        <div className="flex items-center gap-1">
                                            <div className={`w-1.5 h-1.5 rounded-full ${criteria.length ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                            <span>8+ Characters</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className={`w-1.5 h-1.5 rounded-full ${criteria.uppercase ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                            <span>Uppercase Letter</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className={`w-1.5 h-1.5 rounded-full ${criteria.lowercase ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                            <span>Lowercase Letter</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className={`w-1.5 h-1.5 rounded-full ${criteria.number ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                            <span>Number (0-9)</span>
                                        </div>
                                        <div className="flex items-center gap-1 col-span-2">
                                            <div className={`w-1.5 h-1.5 rounded-full ${criteria.special ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                            <span>Special Character (!@#$ etc.)</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Confirm Password</label>
                        <input
                            type="password"
                            required
                            placeholder="Re-enter password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-widest rounded-xl flex items-center justify-center gap-2 shadow-xl shadow-indigo-200 transition-all disabled:opacity-50"
                    >
                        {loading ? (
                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <CreditCard className="w-4 h-4 text-amber-400" />
                                <span>Set Password & Authorize Card</span>
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
